import { FC, useCallback, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import { RendererContext } from '../../../contexts/RendererContext';
import { usePersonaManager } from '../../../hooks/usePersonaManager';
import { ToolSettingsPanel } from '../../../potree/ui/ToolSettingsPanel';

interface ToolSettingsMenuProps {
  visible: boolean;
  onClose?: () => void;
}

/**
 * ToolSettingsMenu - Renders the settings panel for the currently active tool
 *
 * This component bridges the React UI with the vanilla JS/TS settings panel system.
 * When a tool is enabled, it checks if the tool has a settings schema and renders
 * the settings panel in a popover dropdown.
 */
export const ToolSettingsMenu: FC<ToolSettingsMenuProps> = ({ visible, onClose }) => {
  const { viewer } = useContext(RendererContext);
  const { activePersona } = usePersonaManager(viewer);
  const containerRef = useRef<HTMLDivElement>(null);

  // Re-render on tool switches within the active persona
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const persona = activePersona ? viewer?.personaManager?.getPersona(activePersona.name) : undefined;
      if (!persona) {
        return () => undefined;
      }

      persona.addEventListener('tool_enabled', onStoreChange);
      persona.addEventListener('tool_disabled', onStoreChange);

      return () => {
        persona.removeEventListener('tool_enabled', onStoreChange);
        persona.removeEventListener('tool_disabled', onStoreChange);
      };
    },
    [viewer, activePersona]
  );

  // Resolve the active tool's settings panel (stable: tools cache their panel instance)
  const getSettingsPanel = useCallback((): ToolSettingsPanel | null => {
    if (!visible) {
      return null;
    }

    const persona = activePersona ? viewer?.personaManager?.getPersona(activePersona.name) : undefined;
    if (!persona) {
      return null;
    }

    const activeTool = Array.from(persona.tools.values()).find((tool) => tool.enabled);
    if (activeTool && typeof activeTool.getSettingsSchema === 'function' && activeTool.getSettingsSchema()) {
      return activeTool.getSettingsPanel?.() ?? null;
    }
    return null;
  }, [viewer, activePersona, visible]);

  const settingsPanel = useSyncExternalStore(subscribe, getSettingsPanel);

  // Mount/unmount the settings panel element
  useEffect(() => {
    if (!containerRef.current || !settingsPanel) {
      return;
    }

    // Set the onClose callback if provided
    if (onClose && typeof settingsPanel.setOnClose === 'function') {
      settingsPanel.setOnClose(onClose);
    }

    // Append the settings panel element to our container
    const panelElement = settingsPanel.getElement();
    const container = containerRef.current;
    container.appendChild(panelElement);

    // Show the panel
    if (typeof settingsPanel.show === 'function') {
      settingsPanel.show();
    } else {
      panelElement.style.display = 'block';
    }

    // Cleanup
    return () => {
      if (panelElement.parentElement === container) {
        // Hide the panel
        if (typeof settingsPanel.hide === 'function') {
          settingsPanel.hide();
        } else {
          panelElement.style.display = 'none';
        }
        container.removeChild(panelElement);
      }
    };
  }, [settingsPanel, onClose]);

  if (!visible || !settingsPanel) {
    return null;
  }

  return <div ref={containerRef} />;
};
